/**
 * Chat API Client
 *
 * HTTP REST API endpoints for DirectChat functionality:
 * - Get conversations list
 * - Get/create conversation
 * - Get message history
 * - Send message
 * - Mark as read
 * - Get unread count
 *
 * Uses HTTP-only cookies for authentication (via fetchWithTokenRefresh)
 */

import { buildApiUrl } from './config';
import { fetchWithTokenRefresh } from './refreshToken';
import { handleApiError } from './errorHandler';
import type {
  Conversation,
  Message,
  GetConversationResponse,
  CreateConversationResponse,
  UnreadCountResponse,
} from '@/src/widgets/chat/types';

interface PaginationParams {
  limit?: number;
  offset?: number;
}

interface SendMessageDto {
  body: string;
  attachmentIds?: string[];
}

interface MarkAsReadDto {
  upToMessageId: string;
}

/**
 * Chat API
 */
export const chatApi = {
  /**
   * Get all conversations for current actor
   * GET /direct-conversations
   */
  async getConversations(): Promise<Conversation[]> {
    const url = buildApiUrl('/direct-conversations');
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch conversations');
    }

    return response.json();
  },

  /**
   * Create or get existing conversation with a worker
   * POST /direct-conversations
   */
  async getOrCreateConversation(
    workerActorId: string
  ): Promise<CreateConversationResponse> {
    const url = buildApiUrl('/direct-conversations');
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workerActorId }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to create conversation');
    }

    return response.json();
  },

  /**
   * Get conversation by ID with paginated messages
   * GET /direct-conversations/:conversationId
   */
  async getConversation(
    conversationId: string,
    params: PaginationParams = {}
  ): Promise<GetConversationResponse> {
    const queryParams = new URLSearchParams();

    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.offset) {
      queryParams.append('offset', params.offset.toString());
    }

    const query = queryParams.toString();
    const url = buildApiUrl(
      `/direct-conversations/${conversationId}${query ? `?${query}` : ''}`
    );

    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch conversation');
    }

    return response.json();
  },

  /**
   * Send message to conversation (REST fallback, prefer WebSocket)
   * POST /direct-conversations/:conversationId/messages
   */
  async sendMessage(
    conversationId: string,
    body: string,
    attachmentIds?: string[]
  ): Promise<Message> {
    const url = buildApiUrl(`/direct-conversations/${conversationId}/messages`);
    const dto: SendMessageDto = { body, attachmentIds };

    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to send message');
    }

    return response.json();
  },

  /**
   * Mark messages as read up to specified message
   * POST /direct-conversations/:conversationId/mark-read
   */
  async markAsRead(
    conversationId: string,
    upToMessageId: string
  ): Promise<void> {
    const url = buildApiUrl(`/direct-conversations/${conversationId}/mark-read`);
    const dto: MarkAsReadDto = { upToMessageId };

    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to mark messages as read');
    }
  },

  /**
   * Get unread message count for conversation
   * GET /direct-conversations/:conversationId/unread-count
   */
  async getUnreadCount(conversationId: string): Promise<UnreadCountResponse> {
    const url = buildApiUrl(
      `/direct-conversations/${conversationId}/unread-count`
    );

    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to get unread count');
    }

    return response.json();
  },
};
